'use client'

import { useCallback, useMemo } from 'react'
import { sortBy } from 'lodash'
import type { Team, User, UserTeam } from '@skop-app/lib-domain/types'

import { useSelectedTeamSummary } from '@/services/queries/team'
import { useSelectedTeam } from '@/features/team-selection'
import { isSelectableTeamMembership } from '@/features/team-selection'

interface TeamManagementHook {
  team: Team | undefined
  teams: UserTeam[]
  organizationMemberships: UserTeam[]
  isLoadingTeam: boolean
  isLoadingSelectedOrganization: boolean
  organizationParent: Team | undefined
  selectedTeamId: string | null
  isSolo: boolean
  isOrganizationHydrationError: boolean
  retryOrganizationHydration: () => Promise<{ isError: boolean }>
}

const toUserTeamOption = (team: UserTeam): UserTeam => ({ ...team })

export function useTeamManagement(
  userData: User | undefined | null,
): TeamManagementHook {
  const organizations = useMemo(() => userData?.organizations ?? [], [userData])

  // Source of truth for selection (cookie-driven)
  const { selectedTeamId, isSolo } = useSelectedTeam()

  const selectedMembership = organizations.find(
    (organization) => organization.value === selectedTeamId,
  )
  const teamId =
    isSolo || !isSelectableTeamMembership(selectedMembership)
      ? null
      : selectedTeamId

  const teamSummaryQuery = useSelectedTeamSummary({ id: teamId })
  const {
    data: team,
    isLoading: isLoadingSelectedOrganization,
    isError: isTeamSummaryError,
    isFetched: isTeamSummaryFetched,
    refetch: refetchTeamSummary,
  } = teamSummaryQuery

  const parentId = team?.parent?.partner !== 'cyneo' ? team?.parent?.id : null

  const parentSummaryQuery = useSelectedTeamSummary({ id: parentId })
  const { data: organizationParent, isLoading: isLoadingParent } =
    parentSummaryQuery

  const teams = sortBy(
    organizations
      ?.filter(
        (team) => team.status === 'PENDING' || isSelectableTeamMembership(team),
      )
      ?.map(toUserTeamOption) ?? [],
    'label',
  )
  const organizationMemberships = sortBy(
    organizations
      ?.filter((team) => team.status === 'PENDING' || team.status === 'DONE')
      ?.map(toUserTeamOption) ?? [],
    'label',
  )

  const isLoadingTeam =
    Boolean(teamId) &&
    (isLoadingSelectedOrganization || (Boolean(parentId) && isLoadingParent))

  const isOrganizationHydrationError =
    Boolean(teamId) &&
    !isLoadingSelectedOrganization &&
    !team &&
    (isTeamSummaryError || isTeamSummaryFetched)

  const retryOrganizationHydration = useCallback(async () => {
    if (!teamId) return { isError: false }
    const result = await refetchTeamSummary()
    return { isError: result.isError || !result.data }
  }, [refetchTeamSummary, teamId])

  return {
    team: team ?? undefined,
    teams,
    organizationMemberships,
    isLoadingTeam,
    isLoadingSelectedOrganization,
    organizationParent: organizationParent ?? undefined,
    selectedTeamId: teamId,
    isSolo,
    isOrganizationHydrationError,
    retryOrganizationHydration,
  }
}
